import { useEffect, useRef, type MouseEvent as ReactMouseEvent } from 'react';
import styles from './picker-controls.module.scss';

// The colour controls the ramp editor uses: copies of the shared colour picker's
// saturation square, hue slider and HSV maths, kept here so components/color-picker
// is untouched for its other users.

/** A full six-digit hex colour, e.g. "#4589ff". */
export const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

/**
 * Converts a #rrggbb colour to HSV.
 * @param {string} hex The colour.
 * @return {[number, number, number]} [hue 0–360, saturation 0–1, value 0–1].
 */
export function hexToHsv(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d + 6) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
  }
  const s = max === 0 ? 0 : d / max;
  return [h, s, max];
}

/**
 * Converts HSV to a #rrggbb colour.
 * @param {number} h Hue, 0–360.
 * @param {number} s Saturation, 0–1.
 * @param {number} v Value, 0–1.
 * @return {string} The colour.
 */
export function hsvToHex(h: number, s: number, v: number): string {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) {
    r = c;
    g = x;
  } else if (h < 120) {
    r = x;
    g = c;
  } else if (h < 180) {
    g = c;
    b = x;
  } else if (h < 240) {
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }
  const toHex = (n: number) =>
    Math.round((n + m) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

interface SaturationCanvasProps {
  hue: number;
  sat: number;
  val: number;
  onSatValChange: (s: number, v: number) => void;
  onSatValChangeEnd: (s: number, v: number) => void;
}

/**
 * The saturation/value square for a given hue: drag to pick, committing on release.
 * @param {SaturationCanvasProps} props Hue, current saturation/value, and handlers.
 * @return {JSX.Element} The square.
 */
export const SaturationCanvas = ({
  hue,
  sat,
  val,
  onSatValChange,
  onSatValChangeEnd,
}: SaturationCanvasProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dragging = useRef(false);
  const onChangeRef = useRef(onSatValChange);
  const onEndRef = useRef(onSatValChangeEnd);
  onChangeRef.current = onSatValChange;
  onEndRef.current = onSatValChangeEnd;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const { width, height } = canvas;
    ctx.fillStyle = `hsl(${hue}, 100%, 50%)`;
    ctx.fillRect(0, 0, width, height);
    const white = ctx.createLinearGradient(0, 0, width, 0);
    white.addColorStop(0, 'rgba(255,255,255,1)');
    white.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = white;
    ctx.fillRect(0, 0, width, height);
    const black = ctx.createLinearGradient(0, 0, 0, height);
    black.addColorStop(0, 'rgba(0,0,0,0)');
    black.addColorStop(1, 'rgba(0,0,0,1)');
    ctx.fillStyle = black;
    ctx.fillRect(0, 0, width, height);
  }, [hue]);

  const pickCoords = (e: ReactMouseEvent | MouseEvent): [number, number] => {
    const canvas = canvasRef.current;
    if (!canvas) return [sat, val];
    const rect = canvas.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    return [x, 1 - y];
  };

  const onMouseDown = (e: ReactMouseEvent) => {
    e.preventDefault();
    dragging.current = true;
    const [s, v] = pickCoords(e);
    onChangeRef.current(s, v);
    const onMove = (me: MouseEvent) => {
      if (!dragging.current) return;
      const [ms, mv] = pickCoords(me);
      onChangeRef.current(ms, mv);
    };
    const onUp = (me: MouseEvent) => {
      dragging.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      const [es, ev] = pickCoords(me);
      onEndRef.current(es, ev);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return (
    <div className={styles.saturationWrapper}>
      <canvas
        ref={canvasRef}
        width={252}
        height={180}
        className={styles.saturationCanvas}
        onMouseDown={onMouseDown}
      />
      <div
        className={styles.saturationPointer}
        // The pointer sits on the picked colour, so it can only be placed inline.
        style={{ left: `${sat * 100}%`, top: `${(1 - val) * 100}%` }}
      />
    </div>
  );
};

/**
 * The hue slider, 0–360, committing on release.
 * @param {object} props Current hue, a change handler and a release handler.
 * @return {JSX.Element} The slider row.
 */
export const HueSlider = ({
  hue,
  onHueChange,
  onHueChangeEnd,
}: {
  hue: number;
  onHueChange: (hue: number) => void;
  onHueChangeEnd: () => void;
}) => (
  <div className={styles.hueRow}>
    <input
      type="range"
      min={0}
      max={360}
      value={hue}
      aria-label="Hue"
      onChange={(e) => onHueChange(Number(e.target.value))}
      onMouseUp={onHueChangeEnd}
      className={styles.hueSlider}
    />
  </div>
);
